import { Link } from 'react-router-dom'
import { ArrowLeft, BotMessageSquare, Volume2, Music, Mic } from 'lucide-react'
import { CategorizedSoundEffectGallery } from '@/components/gallery/TextToSoundEffectGallery'
import { soundEffectCategories } from '@/app/sound-effects-models/elevenlabs-sound-effects/soundEffectCategories'
import * as stylex from '@stylexjs/stylex'
import { styles } from '@/styles/SoundEffectPage.stylex'

// Model configuration - single source of truth
const MODEL_CONFIG = {
    name: 'ElevenLabs Sound Effects',
    shortName: 'ElevenLabs',
    modelKey: 'elevenlabs/elevenlabs_soundeffect',
    creditsPerRequest: 1,
    providerUrl: 'https://elevenlabs.io/sound-effects',
    providerName: 'ElevenLabs',
    pageUrl: 'https://docs.aibrush.co/sound-effects-models/elevenlabs-sound-effects',
} as const;

export default function ElevenLabsSoundEffectsPage() {

    return (
        <div {...stylex.props(styles.modelPage)}>
            {/* Header Navigation */}
            <Link to="/" {...stylex.props(styles.backLink)}>
                <ArrowLeft size={20} />
                Back to Home
            </Link>

            {/* Hero Section */}
            <section {...stylex.props(styles.heroSection, styles.speechHeroAnimated)}>
                <div {...stylex.props(styles.heroContent)}>
                    <h2 {...stylex.props(styles.heroTitle)}>{MODEL_CONFIG.name.toUpperCase()}</h2>
                    <div {...stylex.props(styles.heroDescription)}>
                        Experience revolutionary <strong>Text to Sound Effects</strong>.
                        Create <strong>cinematic soundscapes</strong> and <strong>immersive audio environments</strong> from simple text descriptions.
                        <br />
                        Powered by <a href={MODEL_CONFIG.providerUrl} target="_blank" rel="noopener noreferrer">{MODEL_CONFIG.providerName}</a>
                    </div>
                    <div {...stylex.props(styles.heroStats)}>
                        <div {...stylex.props(styles.stat)}>
                            <Volume2 size={20} {...stylex.props(styles.statSvg)} />
                            <span>Text to Sound Effects</span>
                        </div>
                        <div {...stylex.props(styles.stat)}>
                            <Music size={20} {...stylex.props(styles.statSvg)} />
                            <span>Cinematic Soundscapes</span>
                        </div>
                    </div>
                    <div {...stylex.props(styles.creditCostBadge)}>{MODEL_CONFIG.creditsPerRequest} credits per request</div>


                    <div {...stylex.props(styles.heroVoicePlayer)}>
                        <div {...stylex.props(styles.audioLabel)}>Animals</div>
                        <audio controls {...stylex.props(styles.heroAudioPlayer)}>
                            <source src="https://cdn.aibrush.co/docs/audio/models/elevenlabs-sound-effects/sample-hero-animals.mp3" type="audio/mpeg" />
                            Your browser does not support the audio element.
                        </audio>
                    </div>
                    <div {...stylex.props(styles.heroVoicePlayer)}>
                        <div {...stylex.props(styles.audioLabel)}>Ambient</div>
                        <audio controls {...stylex.props(styles.heroAudioPlayer)}>
                            <source src="https://cdn.aibrush.co/docs/audio/models/elevenlabs-sound-effects/sample-hero-ambient.mp3" type="audio/mpeg" />
                            Your browser does not support the audio element.
                        </audio>
                    </div>
                </div>

                {/* Floating Icons Container */}
                <div {...stylex.props(styles.floatingIconsContainer)}>
                    {/* Icon 1: Music */}
                    <div {...stylex.props(styles.floatingIcon, styles.floatingIcon1)}>
                        <Music size={120} {...stylex.props(styles.floatingIconSvg)} />
                    </div>

                    {/* Icon 2: Volume2 */}
                    <div {...stylex.props(styles.floatingIcon, styles.floatingIcon2)}>
                        <Volume2 size={80} {...stylex.props(styles.floatingIconSvg)} />
                    </div>

                    {/* Icon 3: Mic */}
                    <div {...stylex.props(styles.floatingIcon, styles.floatingIcon3)}>
                        <Mic size={100} {...stylex.props(styles.floatingIconSvg)} />
                    </div>

                    {/* Icon 4: Bot Message */}
                    <div {...stylex.props(styles.floatingIcon, styles.floatingIcon4)}>
                        <BotMessageSquare size={90} {...stylex.props(styles.floatingIconSvg)} />
                    </div>
                </div>
            </section>

            {/* AI Assistance Note */}
            <div {...stylex.props(styles.importantNote)}>
                <div {...stylex.props(styles.noteIcon)}>
                    <div {...stylex.props(styles.inlineChatButton)}>
                        <BotMessageSquare size={22} />
                    </div>
                </div>
                <div {...stylex.props(styles.noteContent)}>
                    <strong>Need help with sound effect descriptions or prompt writing?</strong> Click the
                    chat icon in the top-right corner of any component to get AI assistance with your sound effect creation!
                </div>
            </div>

            {/* Categorized Sound Effects Gallery */}
            <CategorizedSoundEffectGallery
                title="Sound Effects Categories"
                description={`Explore the exceptional capabilities of <a href='${MODEL_CONFIG.providerUrl}' target='_blank' rel='noopener noreferrer'>${MODEL_CONFIG.name}</a> through these AI-generated audio samples. Click on any category to expand and see available sound effects. Use the search bar to find specific sounds across all categories.`}
                modelKey={MODEL_CONFIG.modelKey}
                modelLabel={MODEL_CONFIG.name}
                modelNote={`This model costs <strong>${MODEL_CONFIG.creditsPerRequest} credits</strong> per request.`}
                categories={soundEffectCategories}
            />
        </div>
    )
}